import React, {
    useEffect, useState, useContext, useCallback
} from 'react';
import { Redirect, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useModal } from '@jutro/components';
import { Wizard } from '@xengage/gw-portals-wizard-react';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { withAuthenticationContext } from '@xengage/gw-digital-auth-react';
import { useTranslator } from '@jutro/locale';
import { ErrorBoundary } from '@xengage/gw-portals-error-react';
import { messages as platformMessages } from '@xengage/gw-platform-translations';
import { ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { PolicyChange, messages as commonMessages } from 'gw-capability-policychange-common-react';
import { FormModalPopover, platformMessages as mlCommonMessages } from 'ml-components-platform-react';
import { MLModalService , MLTagManagerService } from 'ml-portals-services-react';
import { ClausesUtil } from '@xengage/gw-policycommon-util-js';
import { ClauseService } from 'gw-capability-policycommon';
import { MLLandingPageProvider } from 'gw-capability-gateway-quoteandbind-common-react';

import gpaWizardConfig from './config/pup-wizard-config.json5';
import ampWizardConfig from './config/pup-amp-wizard-config.json5';
import messages from './PUPPolicyChange.messages';

function PUPPolicyChangeWizard(props) {
    const modalApi = useModal();
    const wizardConfig = appConfig.portalCode === 'GPA' ? gpaWizardConfig : ampWizardConfig;
    const { steps, title } = wizardConfig;
    const [initialSubmission, setInitialSubmission] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasErrorOccurred, setHasErrorOccurred] = useState(false);
    const viewModelService = useContext(ViewModelServiceContext);
    const { EndorsementService } = useDependencies('EndorsementService');
    const { location, history, authHeader } = props;
    const [additionalProps, setAdditionalProps] = useState({
        isPolicyChangeAndTrasferToPayrollFlow: false
    });

    const translator = useTranslator();

    const {
        showErrorModal,
        showProgressDialog
    } = MLModalService();

    useEffect(() => {
        if (!location.state) {
            history.push('/');
            return;
        }
        const { state: { policyNumber, chosenStepPathML } } = location;
        if (!policyNumber) {
            return;
        }
        const errorModalBox = (errorMessage) => {
            modalApi.showConfirm(errorMessage).then((results) => {
                if (results === 'cancel') {
                    return _.noop();
                }
                setIsLoading(false);
                return history.push(`/contactAgent/${policyNumber}`);
            }, _.noop);
        };
        const promise = EndorsementService.loadEndorsement(policyNumber, authHeader);
        showProgressDialog(messages.loadingPolicyChange, promise);
        promise
            .then((responseData) => {
                const submission = viewModelService.create(
                    new PolicyChange(responseData),
                    'pc',
                    'edge.capabilities.policychange.dto.PolicyChangeDataDTO'
                );
                submission.value.chosenStepPathML = chosenStepPathML;
                setInitialSubmission(submission);
                setIsLoading(false);

                if(submission.value.chosenStepPathML==='/transfer-to-payroll') {
                        setAdditionalProps({...additionalProps,isPolicyChangeAndTrasferToPayrollFlow:true});
                    }

                if (responseData.jobID) {
                    ClausesUtil.getDependentClausesFromServer(
                        ClauseService,
                        _.get(responseData, 'jobID'),
                        authHeader
                    );
                }
            }).catch((error) => {
                console.error(error);
                errorModalBox({
                    title: commonMessages.unableToLoadDraftPolicy,
                    message: commonMessages.somethingWentWrong,
                });
            }).catch(() => {
                errorModalBox({
                    title: commonMessages.unableToStartPolicy,
                    message: commonMessages.anErrorOccurred,
                    status: 'warning',
                    icon: 'mi-error-outline'
                });
            });
        return () => {
            MLTagManagerService.setModel();
        }
    },
    // Disabled so we don't rerun this function on every rerender
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);

    const handleOnCancel = useCallback((cancelParams) => {
        const {
            wizardData: { submissionVM },
            wizardSnapshot,
            currentStepIndex,
            steps: wizardSteps,
            param: nextPath
        } = cancelParams;
        const confirmationPageIndex = wizardSteps.length - 1;
        const withdrawPolicyChange = _.get(submissionVM, 'value.withdrawPolicyChange', false);
        if ((!_.isEmpty(nextPath) && (nextPath.startsWith('/contactAgent' || nextPath.startsWith('/new-quote') || nextPath.startsWith('/contactUs'))))
            || currentStepIndex === confirmationPageIndex || hasErrorOccurred || withdrawPolicyChange
        ) {
            return true;
        }

        return modalApi.showModal(
            <FormModalPopover
                title={translator(mlCommonMessages.areYouSureYouWantToCancel)}
                simpleMessage={translator(mlCommonMessages.areYouSureYouWantToCancelThisPolicyChange)}
                actionBtnLabel={platformMessages.yes}
                cancelBtnLabel={mlCommonMessages.mlCancel}
                closeSimpleMessage={false}
            />
        ).then(() => {
            setIsLoading(true);
            const isValid = submissionVM.aspects.valid && submissionVM.aspects.subtreeValid;
            const payload = (isValid) ? _.get(submissionVM, 'value') : _.get(wizardSnapshot, 'submissionVM.value');
            const jobID = _.get(submissionVM.value, 'jobID');
            const quoteStatus = _.get(wizardSnapshot, 'baseData.periodStatus.value.code')
                || _.get(submissionVM.value, 'status');
            const policyNumber = _.get(submissionVM.value, 'policyNumber');
            let exitPromise;
            let redirectLobPath = (!_.isEmpty(nextPath) && nextPath) || `/change/${jobID}/summary`;
            if (appConfig.persona === 'policyholder') {
                redirectLobPath = (!_.isEmpty(nextPath) && nextPath) || `/account-policy-details/${policyNumber}`;
            }
            if (appConfig.persona === 'policyholder') {
                if (quoteStatus !== 'Bound') {
                    MLTagManagerService.sendLinkClickEvent('Discard All Changes');
                    exitPromise = EndorsementService.withdrawEndorsement([jobID], authHeader);
                } else {
                    return history.push(redirectLobPath);
                }
            } else if (quoteStatus === 'Quoted') {
                exitPromise = EndorsementService.saveEndorsement([payload], authHeader);
            } else if (quoteStatus === 'Draft') {
                exitPromise = EndorsementService.withdrawEndorsement([jobID], authHeader);
            } else {
                return history.push(redirectLobPath);
            }
            exitPromise.then(() => {
                history.push(redirectLobPath);
            }).catch(() => {
                history.push(redirectLobPath);
                showErrorModal(
                    commonMessages.unableToDraftPolicy,
                    messages.anErrorOccurred,
                    platformMessages.ok
                );
            }).finally(() => {
                setIsLoading(false);
            });
            return true;
        }, _.noop);
    }, [hasErrorOccurred, translator, EndorsementService, authHeader, history, showErrorModal]);

    const handleError = useCallback(
        (error) => {
            const state = {
                pathname: '/endorsementContactUs',
                state: error
            };

            setHasErrorOccurred(true);
            return <Redirect push to={state} />
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    if (isLoading) {
        return (
            <></>
        );
    }

    if (!initialSubmission) {
        return null;
    }

    return (
        <ErrorBoundary onError={handleError}>
            <MLLandingPageProvider additionalPolicyChangeProps ={additionalProps}>
            <Wizard
                initialSteps={steps}
                wizardTitle={title}
                initialData={{
                    submissionVM: initialSubmission,
                    alertMessages: []
                }}
                onCancel={handleOnCancel}
                onPreviousModalProps={{
                    title: platformMessages.wantToJump,
                    message: platformMessages.wantToJumpMessage,
                    status: 'warning',
                    icon: 'mi-error-outline',
                    confirmButtonText: platformMessages.yes,
                    cancelButtonText: platformMessages.no
                }}
                extractVMObject={(data) => _.get(data, 'submissionVM')}
            />
            </MLLandingPageProvider>
        </ErrorBoundary>
    );
}

PUPPolicyChangeWizard.propTypes = {
    location: PropTypes.shape({
        state: PropTypes.shape({
            address: PropTypes.shape({}),
            policyNumber: PropTypes.string,
            requestType: PropTypes.string,
            policyDetails: PropTypes.shape({
                effective: PropTypes.instanceOf(Date)
            })
        }),
    }).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired,
    authHeader: PropTypes.shape({
        Authorization: PropTypes.string
    }).isRequired,
};

export default withRouter(withAuthenticationContext(PUPPolicyChangeWizard));
